import { useEffect, useState } from "react";
import axios from "axios";
import { FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { API, adminApi, errMsg, maskDoc, digits, fmtDateTime, getAdminToken } from "@/lib/api";
import { validDoc } from "@/lib/document";
import { toast } from "sonner";

const ativa = (s) => String(s || "").trim().toUpperCase() === "ATIVA";
const Erro = ({ children, testId }) => <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid={testId}>{children}</p>;

function useReceita(doc) {
  const [r, setR] = useState({ st: "idle" });
  useEffect(() => {
    if (doc.length < 14) {
      setR({ st: "idle" });
      return undefined;
    }
    if (!validDoc(doc)) {
      setR({ st: "invalid" });
      return undefined;
    }
    let vivo = true;
    setR({ st: "loading" });
    axios.get(`${API}/fiscal/consulta-cnpj/${doc}`, { headers: { Authorization: `Bearer ${getAdminToken()}` } })
      .then(({ data }) => vivo && setR({ st: "ok", data }))
      .catch((e) => vivo && setR({ st: e.response?.status === 404 ? "notfound" : "error", msg: errMsg(e) }));
    return () => { vivo = false; };
  }, [doc]);
  return r;
}

function ReceitaInfo({ d }) {
  const end = d.endereco || {};
  const rows = [["Razão social", d.nome], ["Nome fantasia", d.nome_fantasia || "—"], ["Situação cadastral", d.situacao_cadastral || "—"], ["Município/UF", [end.municipio, end.uf].filter(Boolean).join("/") || "—"]];
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 rounded-md border px-3 py-2 text-sm" data-testid="detail-document-receita">
      {rows.map(([k, v]) => <div key={k}><dt className="text-xs text-muted-foreground">{k}</dt><dd className={k === "Situação cadastral" && !ativa(v) ? "font-medium text-destructive" : "font-medium"}>{v}</dd></div>)}
    </dl>
  );
}

function Historico({ historico }) {
  if (!historico.length) return null;
  return (
    <ul className="text-xs text-muted-foreground space-y-0.5" data-testid="detail-document-history">
      {[...historico].reverse().map((h) => (
        <li key={h.alterado_em}>{fmtDateTime(h.alterado_em)} · {maskDoc(h.anterior || "")} → {maskDoc(h.novo)}{h.razao_social_confirmada ? ` · ${h.razao_social_confirmada}` : ""} · por {h.alterado_por_nome}</li>
      ))}
    </ul>
  );
}

export function DocumentBox({ tenantId, documento, historico = [], onChanged }) {
  const [doc, setDoc] = useState("");
  const [confirmado, setConfirmado] = useState(false);
  const [busy, setBusy] = useState(false);
  const r = useReceita(doc);
  useEffect(() => { setConfirmado(false); }, [doc]);
  const podeConfirmar = r.st === "ok" && ativa(r.data.situacao_cadastral);
  const save = async () => {
    setBusy(true);
    try {
      await adminApi.patch(`/tenants/${tenantId}/documento`, { documento: doc, confirmado: true });
      toast.success("Documento alterado");
      setDoc("");
      onChanged();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="border-t pt-4 space-y-2" data-testid="detail-document-box">
      <p className="eyebrow">Documento da empresa principal</p>
      <p>Atual: <b className="font-mono" data-testid="detail-document-current">{maskDoc(documento || "")}</b> <span className="text-muted-foreground">· o cliente não pode alterar</span></p>
      <div className="flex flex-wrap items-end gap-2">
        <Input value={maskDoc(doc)} onChange={(e) => setDoc(digits(e.target.value).slice(0, 14))} placeholder="Novo CNPJ" className="w-56" aria-invalid={r.st === "invalid"} data-testid="detail-document-input" />
        <Button variant="outline" onClick={save} disabled={!(podeConfirmar && confirmado) || busy} data-testid="detail-document-change-btn">
          {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileText className="h-4 w-4 mr-1" />}Alterar documento
        </Button>
      </div>
      {r.st === "invalid" && <p className="text-sm font-medium text-destructive" data-testid="detail-document-invalid">CNPJ inválido</p>}
      {r.st === "loading" && <p className="text-sm text-muted-foreground" data-testid="detail-document-loading">Consultando a Receita Federal…</p>}
      {r.st === "notfound" && <Erro testId="detail-document-notfound">CNPJ não encontrado na Receita Federal. Alteração bloqueada.</Erro>}
      {r.st === "error" && <Erro testId="detail-document-error">Não foi possível consultar a Receita Federal ({r.msg}). Alteração bloqueada.</Erro>}
      {r.st === "ok" && <ReceitaInfo d={r.data} />}
      {r.st === "ok" && !podeConfirmar && <Erro testId="detail-document-inactive">Situação cadastral {r.data.situacao_cadastral || "desconhecida"}: o CNPJ não está ATIVO na Receita. Alteração bloqueada.</Erro>}
      {podeConfirmar && (
        <label className="flex items-start gap-2 text-sm" data-testid="detail-document-confirm-label">
          <Checkbox checked={confirmado} onCheckedChange={(v) => setConfirmado(!!v)} className="mt-0.5" data-testid="detail-document-confirm-checkbox" />
          <span>Confirmo que este CNPJ é da empresa <b>{r.data.nome}</b> e foi solicitado pelo cliente</span>
        </label>
      )}
      <Historico historico={historico} />
    </div>
  );
}
